import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { fetchProducts } from "../lib/api";
import { Button } from "../components/ui/button";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "../components/ui/accordion";
import { ArrowRight } from "lucide-react";
import { useCart } from "../context/CartContext";
import { formatUAH } from "../lib/format";
import { toast } from "sonner";

export default function Beauty() {
  const [product, setProduct] = useState(null);
  const [qty, setQty] = useState(1);
  const { addItem } = useCart();

  useEffect(() => {
    fetchProducts().then((all) => {
      setProduct(all.find((p) => p.category === "care") || null);
    }).catch(console.error);
  }, []);

  const onAdd = () => {
    addItem({
      product_id: product.id,
      product_name: product.name_short,
      base_price: product.price,
      size: product.sizes[0] || "30 ml",
      color: "",
      quantity: qty,
      customizations: [],
      image: product.images[0],
    });
    toast.success("Додано до кошика", { description: product.name_short });
  };

  return (
    <div data-testid="beauty-page" className="bg-silea-cream">
      {/* Intro */}
      <section className="max-w-[1400px] mx-auto px-6 lg:px-12 pt-16 pb-10 border-b border-stone-200">
        <p className="eyebrow text-silea-navy/60">Догляд · Fatto con amore</p>
        <h1 className="font-serif-display text-5xl sm:text-7xl mt-4 text-silea-navy leading-[1.05]">
          Ритуал <span className="italic-sing text-silea-yellow">краси</span>
        </h1>
        <p className="mt-4 max-w-xl text-silea-navy/70">
          Мінімалістичний догляд у дусі італійського узбережжя. Створено з любов'ю в Україні.
        </p>
      </section>

      {!product ? (
        <p className="py-24 text-center text-silea-navy/60">Завантаження...</p>
      ) : (
        <section className="max-w-[1400px] mx-auto px-6 lg:px-12 grid grid-cols-1 lg:grid-cols-12 gap-10 py-16">
          {/* Image */}
          <div className="lg:col-span-7">
            <div className="aspect-[4/5] overflow-hidden bg-stone-100">
              <img
                src={product.images[0]}
                alt={product.name}
                fetchPriority="high"
                data-testid="beauty-product-image"
                className="w-full h-full object-cover"
              />
            </div>
          </div>

          {/* Info */}
          <div className="lg:col-span-5 lg:sticky lg:top-28 self-start">
            <p className="eyebrow text-silea-navy/60">Догляд</p>
            <h2 className="font-serif-display text-4xl sm:text-5xl mt-3 text-silea-navy leading-[1.05]">
              {product.name_short}
            </h2>
            <p className="mt-3 text-silea-navy/70">{product.short_description}</p>

            <div className="mt-6 flex items-baseline gap-3">
              <span className="font-serif-display text-3xl text-silea-navy" data-testid="beauty-price">
                {formatUAH(product.price)}
              </span>
              <span className="eyebrow text-silea-navy/50">{product.sizes[0]}</span>
            </div>

            {/* Quantity + Add to cart */}
            <div className="mt-8 flex items-stretch gap-3">
              <div className="inline-flex items-center border border-stone-300">
                <button onClick={() => setQty((q) => Math.max(1, q - 1))} className="px-4 text-silea-navy" data-testid="beauty-qty-decrease">−</button>
                <span className="px-4 text-sm">{qty}</span>
                <button onClick={() => setQty((q) => q + 1)} className="px-4 text-silea-navy" data-testid="beauty-qty-increase">+</button>
              </div>
              <Button
                onClick={onAdd}
                data-testid="beauty-add-to-cart-btn"
                className="flex-1 bg-silea-navy hover:bg-silea-navy/90 text-white rounded-none h-12 eyebrow"
              >
                Додати — {formatUAH(product.price * qty)}
              </Button>
            </div>

            {/* Accordion */}
            <Accordion type="single" collapsible className="mt-10">
              <AccordionItem value="desc" className="border-stone-200">
                <AccordionTrigger className="eyebrow text-silea-navy">Опис</AccordionTrigger>
                <AccordionContent className="text-silea-navy/70 leading-relaxed">
                  {product.description}
                </AccordionContent>
              </AccordionItem>
              <AccordionItem value="ingredients" className="border-stone-200">
                <AccordionTrigger className="eyebrow text-silea-navy">Склад</AccordionTrigger>
                <AccordionContent className="text-silea-navy/70">{product.fabric}</AccordionContent>
              </AccordionItem>
              <AccordionItem value="usage" className="border-stone-200">
                <AccordionTrigger className="eyebrow text-silea-navy">Застосування</AccordionTrigger>
                <AccordionContent className="text-silea-navy/70">{product.care}</AccordionContent>
              </AccordionItem>
            </Accordion>

            <Link to="/products" className="inline-flex items-center gap-3 mt-10 silea-link eyebrow text-silea-navy" data-testid="beauty-to-collection">
              До колекції купальників <ArrowRight size={16} strokeWidth={1.6} />
            </Link>
          </div>
        </section>
      )}
    </div>
  );
}
